import { useState, useRef, useId } from "react";

interface TooltipProps {
  text: string;
  position?: "top" | "bottom";
  delay?: number;
  children: React.ReactNode;
}

export function Tooltip({ text, position = "top", delay = 300, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  function show() {
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }

  function hide() {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setVisible(false);
  }

  const isTop = position === "top";

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={visible ? id : undefined}
    >
      {children}
      {visible && (
        <span
          id={id}
          role="tooltip"
          className={`absolute z-[var(--z-tooltip)] px-2.5 py-1.5 rounded-lg text-[12px] leading-snug font-normal
            bg-surface border border-border text-text-primary shadow-lg
            max-w-[220px] w-max pointer-events-none
            left-1/2
            ${isTop ? "bottom-full mb-2 -translate-x-1/2 animate-tooltip-top" : "top-full mt-2 -translate-x-1/2 animate-tooltip-bottom"}`}
        >
          {text}
          {/* Arrow */}
          <span
            className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-surface border-border
              ${isTop ? "top-full -mt-1 border-r border-b" : "bottom-full -mb-1 border-l border-t"}`}
          />
        </span>
      )}
    </span>
  );
}
